import axios, { AxiosError } from "axios";

let interceptorId: number | null = null;

/**
 * Registers the global "plain 401 → logout redirect" Axios response
 * interceptor. Each app calls this once at its entry point (src/index.tsx,
 * next to setAppType) with its own unauthorized-redirect handler — replacing
 * the old import-time side effect in reactQuery/index.ts.
 *
 * MUST run BEFORE the app renders: axios executes response interceptors in
 * registration order, and this generic handler has to stay AHEAD of the
 * session-kill interceptor that SessionInterceptorProvider registers in a
 * mount effect (setupSessionInterceptor). Re-calling this after the session
 * interceptor is registered would eject + re-append it BEHIND the session
 * interceptor, inverting the chain — so never re-register at runtime.
 * (Calling it repeatedly before render / across test files is safe: the
 * eject-before-reregister guard keeps exactly one instance.)
 */
export const setupUnauthorizedInterceptor = (onUnauthorized: () => void): void => {
  // Prevent duplicate interceptors (important under vitest isolate:false,
  // where the module registry — and axios — is shared across test files).
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
  }

  interceptorId = axios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        onUnauthorized();
      }
      return Promise.reject(error);
    },
  );
};

/**
 * Removes the unauthorized interceptor. Tests that register it explicitly
 * should call this in afterAll so it doesn't leak into later files sharing
 * the worker's module registry.
 */
export const removeUnauthorizedInterceptor = (): void => {
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
    interceptorId = null;
  }
};
